/* eslint-disable @typescript-eslint/naming-convention */

export const bedrockVariants = [
  'Bedrock:Titan',
  'Bedrock:Anthropic',
  'Bedrock:Mistral',
  'Bedrock:Meta',
  'Bedrock:Stability',
  'Bedrock:Cohere',
  'Bedrock:Nova',
] as const;

export type BedrockVariant = (typeof bedrockVariants)[number];

export const bedrockSubProviders = ['Anthropic', 'Mistral', 'Titan', 'Meta', 'Stability', 'Cohere', 'Nova'] as const;

export const bedrockVariantSchemas = {
  'Bedrock:Anthropic': {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {
      stop_sequences: {
        type: 'array',
        description: 'Sequences that will cause the model to stop generating.',
        items: {
          type: 'string',
        },
        default: [],
      },
      temperature: {
        type: 'number',
        description:
          'The amount of randomness injected into the response. Use a value closer to 0 for analytical tasks, and closer to 1 for creative and generative tasks.',
        default: 1,
        minimum: 0,
        maximum: 1,
      },
      top_p: {
        type: 'number',
        description:
          'Nucleus sampling parameter that specifies the cumulative probability for each token, where the model considers only the tokens that account for top_p probability mass.',
        default: 1,
        minimum: 0,
        maximum: 1,
      },
      top_k: {
        type: 'integer',
        description:
          'Limits sampling to the top K options for each subsequent token to avoid long-tail low-probability responses.',
        default: 250,
        minimum: 0,
        maximum: 500,
      },
      max_tokens: {
        type: 'integer',
        description: 'The maximum number of tokens to generate before stopping.',
        minimum: 1,
        maximum: 8192,
      },
    },
    required: [],
  },
  'Bedrock:Mistral': {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {
      max_tokens: {
        type: 'integer',
        description:
          'Specify the maximum number of tokens to use in the generated response. The model truncates the response once the generated text exceeds max_tokens.',
        default: 512,
        minimum: 1,
        maximum: 8192,
        enum: [512, 4096, 8192],
      },
      stop: {
        type: 'array',
        description:
          'A list of stop sequences that, if generated by the model, stops the model from generating further output.',
        items: {
          type: 'string',
        },
        default: [],
        maxItems: 10,
      },
      temperature: {
        type: 'number',
        description: 'Controls the randomness of predictions made by the model.',
        default: 0.5,
        minimum: 0,
        maximum: 1,
        enum: [0.5, 0.7],
      },
      top_p: {
        type: 'number',
        description:
          'Controls the diversity of text that the model generates by setting the percentage of most-likely candidates that the model considers for the next token.',
        default: 0.9,
        minimum: 0,
        maximum: 1,
        enum: [0.9, 1],
      },
      top_k: {
        type: 'integer',
        description: 'Controls the number of most-likely candidates that the model considers for the next token.',
        default: 50,
        minimum: 1,
        maximum: 200,
        enum: [50],
      },
    },
    required: [],
  },
  'Bedrock:Titan': {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {
      inputText: {
        type: 'string',
        description:
          "The prompt to provide the model for generating a response. To generate responses in a conversational style, wrap the prompt with 'User: <prompt>\nBot:'",
        minLength: 1,
      },
      textGenerationConfig: {
        type: 'object',
        properties: {
          temperature: {
            type: 'number',
            description: 'Use a lower value to decrease randomness in responses.',
            default: 0.7,
            minimum: 0.0,
            maximum: 1.0,
          },
          topP: {
            type: 'number',
            description: 'Use a lower value to ignore less probable options and decrease the diversity of responses.',
            default: 0.9,
            minimum: 0.0,
            maximum: 1.0,
          },
          maxTokenCount: {
            type: 'integer',
            description:
              'Specify the maximum number of tokens to generate in the response. Maximum token limits are strictly enforced.',
            default: 512,
            minimum: 0,
            maximum: 8192,
            enum: [512, 4096, 8192, 3072],
          },
          stopSequences: {
            type: 'array',
            description: 'Specify a character sequence to indicate where the model should stop.',
            items: {
              type: 'string',
            },
            maxItems: 10,
          },
        },
        additionalProperties: false,
      },
    },
    required: ['inputText'],
    additionalProperties: false,
  },
  'Bedrock:Meta': {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {
      temperature: {
        type: 'number',
        description: 'Use a lower value to decrease randomness in the response.',
        default: 0.5,
        minimum: 0.0,
        maximum: 1.0,
      },
      top_p: {
        type: 'number',
        description: 'Use a lower value to ignore less probable options. Set to 0 or 1.0 to disable.',
        default: 0.9,
        minimum: 0.0,
        maximum: 1.0,
      },
      max_gen_len: {
        type: 'integer',
        description:
          'Specify the maximum number of tokens to use in the generated response. The model truncates the response once the generated text exceeds this limit.',
        default: 512,
        minimum: 1,
        maximum: 2048,
      },
    },
    required: [],
    additionalProperties: false,
  },
  'Bedrock:Stability': {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {
      cfg_scale: {
        type: 'number',
        description:
          'Determines how much the final image portrays the prompt. Use a lower number to increase randomness in the generation.',
        minimum: 0,
        maximum: 35,
        default: 7,
      },
      clip_guidance_preset: {
        type: 'string',
        description: 'Enum for selecting the clip guidance preset.',
        enum: ['FAST_BLUE', 'FAST_GREEN', 'NONE', 'SIMPLE SLOW', 'SLOWER', 'SLOWEST'],
      },
      height: {
        type: 'integer',
        description: 'Height of the image to generate, in pixels, in an increment divisible by 64.',
        enum: [1024, 1152, 1216, 1344, 1536, 640, 768, 832, 896],
      },
      width: {
        type: 'integer',
        description: 'Width of the image to generate, in pixels, in an increment divisible by 64.',
        enum: [1024, 1152, 1216, 1344, 1536, 640, 768, 832, 896],
      },
      sampler: {
        type: 'string',
        description:
          'The sampler to use for the diffusion process. If omitted, the model automatically selects a sampler.',
        enum: [
          'DDIM',
          'DDPM',
          'K_DPMPP_2M',
          'K_DPMPP_2S_ANCESTRAL',
          'K_DPM_2',
          'K_DPM_2_ANCESTRAL',
          'K_EULER',
          'K_EULER_ANCESTRAL',
          'K_HEUN',
          'K_LMS',
        ],
      },
      samples: {
        type: 'integer',
        description: 'The number of images to generate. Amazon Bedrock supports generating one image.',
        default: 1,
        minimum: 1,
        maximum: 1,
      },
      seed: {
        type: 'integer',
        description:
          'The seed determines the initial noise setting. If not set or set to 0, it will be a random number.',
        minimum: 0,
        maximum: 4294967295,
        default: 0,
      },
      steps: {
        type: 'integer',
        description:
          'Generation step determines how many times the image is sampled. More steps can result in a more accurate result.',
        minimum: 10,
        maximum: 150,
        default: 30,
      },
      style_preset: {
        type: 'string',
        description: 'A style preset that guides the image model towards a particular style.',
        enum: [
          '3d-model',
          'analog-film',
          'animé',
          'cinematic',
          'comic-book',
          'digital-art',
          'enhance',
          'fantasy-art',
          'isometric',
          'line-art',
          'low-poly',
          'modeling-compound',
          'neon-punk',
          'origami',
          'photographic',
          'pixel-art',
          'tile-texture',
        ],
      },
      extras: {
        type: 'object',
        description:
          'Extra parameters passed to the engine, used for in-development or experimental features. Use with caution.',
      },
    },
    required: [],
    additionalProperties: false,
  },
  'Bedrock:Cohere': {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {
      return_likelihoods: {
        type: 'string',
        description: 'Specify how and if the token likelihoods are returned with the response.',
        enum: ['GENERATION', 'ALL', 'NONE'],
        default: 'NONE',
      },
      stream: {
        type: 'boolean',
        description:
          'Specify true to return the response piece-by-piece in real-time, and false to return the complete response after the process finishes.',
      },
      logit_bias: {
        type: 'object',
        description:
          'Prevents the model from generating unwanted tokens or incentivizes the model to include desired tokens. Format is {token_id: bias} where bias is a float between -10 and 10.',
        patternProperties: {
          '^\\d+$': {
            type: 'number',
            minimum: -10,
            maximum: 10,
          },
        },
        additionalProperties: false,
      },
      num_generations: {
        type: 'integer',
        description: 'The maximum number of generations the model should return.',
        default: 1,
        minimum: 1,
        maximum: 5,
      },
      truncate: {
        type: 'string',
        description: 'Specifies how the API handles inputs longer than the maximum token length.',
        enum: ['NONE', 'START', 'END'],
        default: 'END',
      },
      temperature: {
        type: 'number',
        description: 'Use a lower value to decrease randomness in the response.',
        default: 0.9,
        minimum: 0,
        maximum: 5,
      },
      p: {
        type: 'number',
        description: 'Top P. Use a lower value to ignore less probable options. Set to 0 or 1.0 to disable.',
        default: 0.75,
        minimum: 0,
        maximum: 1,
      },
      k: {
        type: 'integer',
        description: 'Top K. Specify the number of token choices the model uses to generate the next token.',
        default: 0,
        minimum: 0,
        maximum: 500,
      },
      max_tokens: {
        type: 'integer',
        description: 'Specify the maximum number of tokens to use in the generated response.',
        default: 20,
        minimum: 1,
        maximum: 4096,
      },
      stop_sequences: {
        type: 'array',
        description:
          'Configure up to four sequences that the model recognizes. After a stop sequence, the model stops generating further tokens.',
        items: {
          type: 'string',
        },
        maxItems: 4,
      },
    },
    required: ['stream'],
    additionalProperties: false,
  },
  'Bedrock:Nova': {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {
      maxTokens: {
        type: 'integer',
        minimum: 1,
        maximum: 5000,
        default: 'dynamic*',
      },
      temperature: {
        type: 'number',
        minimum: 0,
        maximum: 1,
        default: 0.7,
      },
      topP: {
        type: 'number',
        minimum: 0,
        maximum: 1,
        default: 0.9,
      },
      topK: {
        type: 'integer',
        minimum: 0,
        default: 50,
      },
      stopSequences: {
        type: 'array',
        items: {
          type: 'string',
        },
        default: [],
      },
    },
    additionalProperties: false,
    required: ['maxTokens', 'temperature', 'topP', 'topK', 'stopSequences'],
  },
} as const;

export const retrieveBedrockParametersByVariant = (variant: BedrockVariant): string[] => {
  switch (variant) {
    case 'Bedrock:Anthropic':
      return Object.keys(bedrockVariantSchemas['Bedrock:Anthropic'].properties);
    case 'Bedrock:Mistral':
      return Object.keys(bedrockVariantSchemas['Bedrock:Mistral'].properties);
    case 'Bedrock:Titan':
      return Object.keys(bedrockVariantSchemas['Bedrock:Titan'].properties);
    case 'Bedrock:Meta':
      return Object.keys(bedrockVariantSchemas['Bedrock:Meta'].properties);
    case 'Bedrock:Cohere':
      return Object.keys(bedrockVariantSchemas['Bedrock:Cohere'].properties);
    case 'Bedrock:Stability':
      return Object.keys(bedrockVariantSchemas['Bedrock:Stability'].properties);
    case 'Bedrock:Nova':
      return Object.keys(bedrockVariantSchemas['Bedrock:Nova'].properties);
    default:
      return [];
  }
};
